import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, Search, Filter, ChevronDown, RefreshCw, Plus, Trash2, Edit3, RotateCcw, Download, LogIn, LogOut } from 'lucide-react';
import { adminAPI } from '../../services/api';

interface AuditEntry {
  id: number;
  user_id: number | null;
  action: string;
  entity_type: string;
  entity_id: number | null;
  details: any;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  user_name: string | null;
  user_email: string | null;
}

const actionIcons: Record<string, React.ReactNode> = {
  create: <Plus size={14} className="text-green-500" />,
  update: <Edit3 size={14} className="text-blue-500" />,
  delete: <Trash2 size={14} className="text-red-500" />,
  restore: <RotateCcw size={14} className="text-purple-500" />,
  export: <Download size={14} className="text-amber-500" />,
  login: <LogIn size={14} className="text-green-500" />,
  logout: <LogOut size={14} className="text-gray-500" />,
};

const actionColors: Record<string, string> = {
  create: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  update: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  delete: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  restore: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  export: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  login: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  logout: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400',
};

const actionLabels: Record<string, string> = {
  create: 'Creado',
  update: 'Editado',
  delete: 'Eliminado',
  restore: 'Restaurado',
  export: 'Exportado',
  login: 'Login',
  logout: 'Logout',
};

const entityLabels: Record<string, string> = {
  headache: 'Dolor',
  tension: 'Tension',
  lloro: 'Lloro',
  user: 'Usuario',
};

export const AdminAudit: React.FC = () => {
  const [logs, setLogs] = useState<AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    loadLogs();
  }, [filterAction, filterEntity]);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const params: any = { limit: 100 };
      if (filterAction) params.action = filterAction;
      if (filterEntity) params.entityType = filterEntity;
      const response = await adminAPI.getAuditLogs(params);
      if (response.success) {
        setLogs(response.data?.logs || []);
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredLogs = searchUser
    ? logs.filter(l =>
        (l.user_name || '').toLowerCase().includes(searchUser.toLowerCase()) ||
        (l.user_email || '').toLowerCase().includes(searchUser.toLowerCase())
      )
    : logs;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return 'Ahora mismo';
    if (mins < 60) return `Hace ${mins}min`;
    if (hours < 24) return `Hace ${hours}h`;
    if (days < 7) return `Hace ${days}d`;
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getDetailsSummary = (entry: AuditEntry): string => {
    if (!entry.details) return '';
    const d = typeof entry.details === 'string' ? JSON.parse(entry.details) : entry.details;
    if (d.intensity) return `${d.intensity}${d.location ? ' - ' + d.location : ''}`;
    if (d.systolic) return `${d.systolic}/${d.diastolic} mmHg`;
    return '';
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[var(--color-text)] flex items-center gap-2">
            <ClipboardList size={24} style={{ color: '#6366F1' }} />
            Audit Log
          </h2>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Registro de todas las acciones del sistema
          </p>
        </div>
        <button
          onClick={loadLogs}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)] text-sm font-medium hover:bg-[var(--color-bg)] transition-colors self-start"
        >
          <RefreshCw size={16} />
          Actualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input
            type="text"
            placeholder="Buscar por usuario..."
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
            className="input pl-9 text-sm"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="input text-sm flex-1 min-w-0 sm:flex-none sm:w-auto sm:min-w-[130px]"
          >
            <option value="">Todas las acciones</option>
            <option value="create">Creados</option>
            <option value="update">Editados</option>
            <option value="delete">Eliminados</option>
            <option value="restore">Restaurados</option>
          </select>
          <select
            value={filterEntity}
            onChange={(e) => setFilterEntity(e.target.value)}
            className="input text-sm flex-1 min-w-0 sm:flex-none sm:w-auto sm:min-w-[120px]"
          >
            <option value="">Todos los tipos</option>
            <option value="headache">Dolores</option>
            <option value="tension">Tensiones</option>
            <option value="lloro">Lloros</option>
          </select>
        </div>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-3 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center py-12">
          <ClipboardList size={48} className="mx-auto mb-4 text-[var(--color-text-muted)] opacity-30" />
          <p className="text-[var(--color-text-muted)]">No hay registros de audit</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredLogs.map((entry, index) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.02, 0.5) }}
              className="card overflow-hidden"
            >
              <button
                onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                className="w-full p-3 sm:p-4 flex items-center gap-3 text-left"
              >
                {/* Icono accion */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${actionColors[entry.action] || 'bg-gray-100'}`}>
                  {actionIcons[entry.action] || <Filter size={14} />}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${actionColors[entry.action] || ''}`}>
                      {actionLabels[entry.action] || entry.action}
                    </span>
                    <span className="text-sm font-medium text-[var(--color-text)]">
                      {entityLabels[entry.entity_type] || entry.entity_type}
                    </span>
                    {entry.entity_id && (
                      <span className="text-xs text-[var(--color-text-muted)]">#{entry.entity_id}</span>
                    )}
                    {getDetailsSummary(entry) && (
                      <span className="text-xs text-[var(--color-text-muted)] truncate max-w-[150px]">
                        - {getDetailsSummary(entry)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {entry.user_name || entry.user_email || 'Sistema'}
                    </span>
                    <span className="text-xs text-[var(--color-text-muted)] opacity-50">
                      {formatDate(entry.created_at)}
                    </span>
                  </div>
                </div>

                <ChevronDown
                  size={16}
                  className={`text-[var(--color-text-muted)] flex-shrink-0 transition-transform ${expandedId === entry.id ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Detalles expandidos */}
              {expandedId === entry.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-[var(--color-border)]"
                >
                  <div className="pt-3 space-y-2 text-sm">
                    {entry.user_email && (
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-muted)]">Email:</span>
                        <span className="text-[var(--color-text)]">{entry.user_email}</span>
                      </div>
                    )}
                    {entry.ip_address && (
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-muted)]">IP:</span>
                        <span className="text-[var(--color-text)] font-mono text-xs">{entry.ip_address}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-muted)]">Fecha exacta:</span>
                      <span className="text-[var(--color-text)]">
                        {new Date(entry.created_at).toLocaleString('es-ES')}
                      </span>
                    </div>
                    {entry.details && (
                      <div>
                        <span className="text-[var(--color-text-muted)] block mb-1">Detalles:</span>
                        <pre className="p-2 rounded-lg bg-[var(--color-bg)] text-xs text-[var(--color-text)] overflow-x-auto">
                          {JSON.stringify(typeof entry.details === 'string' ? JSON.parse(entry.details) : entry.details, null, 2)}
                        </pre>
                      </div>
                    )}
                    {entry.user_agent && (
                      <div>
                        <span className="text-[var(--color-text-muted)] block mb-1">User Agent:</span>
                        <p className="text-xs text-[var(--color-text-muted)] break-all">{entry.user_agent}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};
